import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Form, Alert } from "react-bootstrap";
import "./CreateInvoice.css";
import { updateInvoice, updateClaim } from "../../afinanceapi";

// ---------- HELPER FUNCTIONS (no locale) ----------

// Safely turn any value into a number, tolerant of commas, spaces, etc.
function normalizeToNumber(value) {
  if (typeof value === "number") return value;
  if (value == null) return 0;

  const str = String(value)
    .replace(/\s/g, "") // remove spaces
    .replace(/,/g, ".") // comma → dot
    .replace(/[^0-9.-]/g, ""); // keep digits, dot, minus

  const num = parseFloat(str);
  return Number.isNaN(num) ? 0 : num;
}

// Always return a string with "." as decimal separator, 2 decimals
function formatAmount(value) {
  const num = normalizeToNumber(value);
  return num.toFixed(2); // JS toFixed always uses "."
}

export default function ChangeLiabilityModal({
  show,
  onHide,
  lines = [],
  onSave,
  invoiceId,
  claimId,
  invoice,
  claim,
}) {
  const [lineValues, setLineValues] = useState([]);
  const [newClaimStatus, setNewClaimStatus] = useState("");

  useEffect(() => {
    if (Array.isArray(lines)) {
      const init = lines.map((line) => ({
        ...line,
        patientAmount: formatAmount(line.patientAmount || 0),
        medAidAmount: formatAmount(line.medAidAmount || 0),
      }));
      setLineValues(init);
    }
  }, [lines]);

  const handleAmountChange = (index, field, rawValue) => {
    const updated = [...lineValues];

    // 1) Strip unwanted chars, normalise comma→dot but keep as raw while typing
    const clean = rawValue
      .replace(/\s/g, "")
      .replace(/,/g, ".")
      .replace(/[^0-9.]/g, "");

    updated[index][field] = clean;

    // 2) Recalculate counterpart, keep it as raw (not formatted yet)
    const rate = normalizeToNumber(updated[index].rate);
    const num = normalizeToNumber(clean);

    const otherField =
      field === "patientAmount" ? "medAidAmount" : "patientAmount";
    const counterpart = Math.max(rate - num, 0);

    updated[index][otherField] = counterpart.toString();

    setLineValues(updated);
  };

  const handleBlur = (index, field) => {
    const updated = [...lineValues];

    const raw = String(updated[index][field] || "");
    const num = normalizeToNumber(raw);

    // 1) Format current field
    updated[index][field] = formatAmount(num);

    // 2) Recalculate & format counterpart
    const rate = normalizeToNumber(updated[index].rate);
    const otherField =
      field === "patientAmount" ? "medAidAmount" : "patientAmount";
    const counterpart = Math.max(rate - num, 0);

    updated[index][otherField] = formatAmount(counterpart);

    setLineValues(updated);
  };

  const fmt = (val) => formatAmount(val);

  const handleSave = async () => {
    if (onSave) onSave(lineValues);

    // --- 1️⃣ Recalculate new invoice totals ---
    const patientTotal = lineValues.reduce(
      (acc, l) => acc + normalizeToNumber(l.patientAmount),
      0
    );
    const medAidTotal = lineValues.reduce(
      (acc, l) => acc + normalizeToNumber(l.medAidAmount),
      0
    );

    // --- 2️⃣ Build updated invoice object ---
    const patientTotalRounded = Number(patientTotal.toFixed(2));
    const medAidTotalRounded = Number(medAidTotal.toFixed(2));

    const invoiceToSave = {
      ...invoice,
      lines: lineValues,
      patientTotal: patientTotalRounded,
      totalPatientAmount: patientTotalRounded,
      medAidTotal: medAidTotalRounded,
      totalMedicalAidAmount: medAidTotalRounded,
      outstandingBalance: {
        ...(invoice.outstandingBalance || {}),
        patientOutstanding: patientTotalRounded,
        medAidOutstanding: medAidTotalRounded,
      },
    };

    // --- 3️⃣ Save Invoice ---
    await updateInvoice(invoiceId, invoiceToSave);

    // --- 4️⃣ If Claim Exists, Update Claim Financials ---
    if (claimId && claim) {
      const updatedClaim = { ...claim };

      // Update claim status
      if (newClaimStatus) updatedClaim.status = newClaimStatus;

      // Update each claim line’s approved amounts
      updatedClaim.lineResults = updatedClaim.lineResults.map((cLine) => {
        const match = lineValues.find(
          (l) =>
            l.lineNumber === cLine.lineNumber ||
            l.tariffCode === cLine.tariffCode
        );
        if (match) {
          const medAid = formatAmount(match.medAidAmount || 0);
          return {
            ...cLine,
            approvedGrossAmount: medAid,
            approvedNettAmount: medAid,
          };
        }
        return cLine;
      });

      // Calculate new totals
      const totalApprovedNum = updatedClaim.lineResults.reduce(
        (acc, l) => acc + normalizeToNumber(l.approvedNettAmount),
        0
      );
      const totalApproved = formatAmount(totalApprovedNum);

      updatedClaim.financials = {
        ...updatedClaim.financials,
        approvedGross: totalApproved,
        approvedNett: totalApproved,
      };

      // Save claim
      await updateClaim(claimId, updatedClaim);
    }

    // --- 5️⃣ Refresh & Close ---
    window.location.reload();
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      dialogClassName="modal-75w"
    >
      <Modal.Header
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
        closeButton
      >
        <Modal.Title as="h6" className="m-0">
          Change Liability
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {lineValues.length === 0 ? (
          <p className="text-muted text-center">No lines available.</p>
        ) : (
          <>
            <span className="text-primary mb-1">
              Update the Patient or Medical Aid Amounts as needed:
            </span>

            <Table bordered hover responsive size="sm" className="align-middle">
              <thead className="table-primary text-center">
                <tr>
                  <th
                    className="text-primary fw-semibold"
                    style={{ width: "10%" }}
                  >
                    Date
                  </th>
                  <th
                    className="text-primary fw-semibold"
                    style={{ width: "10%" }}
                  >
                    Tariff Code
                  </th>
                  <th
                    className="text-primary fw-semibold"
                    style={{ width: "50%" }}
                  >
                    Description
                  </th>
                  <th
                    className="text-primary fw-semibold"
                    style={{ width: "10%" }}
                  >
                    Total (R)
                  </th>
                  <th
                    className="text-primary fw-semibold"
                    style={{ width: "10%" }}
                  >
                    Patient (R)
                  </th>
                  <th
                    className="text-primary fw-semibold"
                    style={{ width: "10%" }}
                  >
                    Medical Aid (R)
                  </th>
                </tr>
              </thead>

              <tbody>
                {lineValues.map((line, idx) => {
                  const claimLine =
                    claim?.lineResults?.find(
                      (c) =>
                        c.lineNumber === line.lineNumber ||
                        c.tariffCode === line.tariffCode
                    ) || {};

                  const approvedRaw =
                    claimLine?.approvedNettAmount ||
                    claimLine?.approvedGrossAmount ||
                    "0.00";

                  const approved = normalizeToNumber(approvedRaw);
                  const isApprovedPositive = approved > 0;

                  const formattedDate =
                    line.appointmentDateCustom ||
                    line.appointmentDateSet ||
                    (claimLine?.dateOfService
                      ? `${claimLine.dateOfService.slice(
                          0,
                          4
                        )}-${claimLine.dateOfService.slice(
                          4,
                          6
                        )}-${claimLine.dateOfService.slice(6, 8)}`
                      : "-");

                  return (
                    <React.Fragment key={idx}>
                      {/* --- Approved Row --- */}
                      {claimLine && claimId && (
                        <tr
                          className={
                            isApprovedPositive
                              ? "approved-row-green"
                              : "approved-row-red"
                          }
                        >
                          <td
                            colSpan={5}
                            className="text-start ps-4 small border-top fw-semibold text-primary align-middle"
                          >
                            <i
                              className={`fa ${
                                isApprovedPositive
                                  ? "fa-check-circle text-success"
                                  : "fa-times-circle text-danger"
                              } me-2`}
                            ></i>
                            Approved Amount (from claim)
                          </td>
                          <td
                            className={`text-end me-2 fw-semibold small ${
                              isApprovedPositive
                                ? "text-success"
                                : "text-danger"
                            }`}
                          >
                            R {approved.toFixed(2)}
                          </td>
                        </tr>
                      )}

                      {/* --- Invoice Line Row --- */}
                      <tr>
                        <td className="text-center align-middle">
                          {formattedDate}
                        </td>
                        <td className="text-center align-middle">
                          {line.tariffCode || claimLine.tariffCode || "-"}
                        </td>
                        <td className="align-middle">
                          {line.description || claimLine.description || "-"}
                        </td>
                        <td className="text-end fw-semibold text-primary align-middle">
                          {fmt(line.rate)}
                        </td>
                        <td className="align-middle">
                          <Form.Control
                            type="text"
                            size="sm"
                            className="text-end flex-grow-1 border-0"
                            value={line.patientAmount}
                            onChange={(e) =>
                              handleAmountChange(
                                idx,
                                "patientAmount",
                                e.target.value
                              )
                            }
                            onBlur={() => handleBlur(idx, "patientAmount")}
                          />
                        </td>
                        <td className="align-middle">
                          <Form.Control
                            type="text"
                            size="sm"
                            className="text-end flex-grow-1 border-0"
                            value={line.medAidAmount}
                            onChange={(e) =>
                              handleAmountChange(
                                idx,
                                "medAidAmount",
                                e.target.value
                              )
                            }
                            onBlur={() => handleBlur(idx, "medAidAmount")}
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>

            {/* Advisory + Status Dropdown */}
            {claimId && (
              <Alert variant="warning" className="text-dark small mt-3">
                <p className="mb-2">
                  <strong>Note:</strong> The invoice has a claim associated with
                  it. Changing the liability will <strong>not</strong> alter the
                  original claim submitted to the Medical Aid.
                </p>
                <p className="mb-2">
                  Only use <em>Change Liability</em> after the Medical Aid has
                  processed the claim and an official outcome was received. If
                  you choose to go ahead, please select the appropriate new
                  status for the claim below:
                </p>
                <Form.Select
                  size="sm"
                  className="mt-2 w-auto"
                  value={newClaimStatus}
                  onChange={(e) => setNewClaimStatus(e.target.value)}
                >
                  <option value="">-- Select New Claim Status --</option>
                  <option value="Finalised">Finalised</option>
                  <option value="Processed - Not Paid">
                    Processed - Not Paid
                  </option>
                  <option value="Processed - Claim Reversed">
                    Processed - Claim Reversed
                  </option>
                </Form.Select>
              </Alert>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={claimId && !newClaimStatus}
        >
          <i className="fa-solid fa-save me-2"></i>Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
